import { memo, useEffect, useCallback, useState, ChangeEvent, ReactNode } from 'react'

import {
  Button,
  CalculatorIcon,
  CalendarIcon,
  ChatIcon,
  ComercialBuildingIcon,
  GroundIcon,
  MoneyCircleIcon,
  MoneyCircleDashIcon,
  PersonIcon,
  RadioButton,
  SelectDropdown,
  SmartphoneIcon,
  Text,
  TextField,
  UsersGroupIcon
} from '@cash-me/react-components'
import extenso from 'extenso'
import { useRouter } from 'next/router'

import { Breadcrumb } from 'components/breadcrumb'
import { Grid, Col } from 'components/grid'
import { FormErrors, FormValues, ProspectPersonType, ValidationError } from 'src/models/loan-form'
import { buildSimulatorUrl } from 'utils/buildSimulatorUrl'
import { pushDataLayer } from 'utils/push-data-layer'
import { setSessionValue } from 'utils/session'

import { stateOptions } from './states'
import * as S from './styles'

type Props = {
  dataLayerPageName?: string
  content?: PropertyBackedLoanHeroContent
  variant?: PropertyBackedLoanHeroVariant
  benefitIcons?: [ReactNode, ReactNode, ReactNode]
  formConfig?: PropertyBackedLoanHeroFormConfig
}

export type PropertyBackedLoanHeroVariant =
  | 'default'
  | 'land-loan'
  | 'home-renovation'
  | 'construction-loan'
  | 'solar-energy'

export type PropertyBackedLoanHeroFormConfig = {
  loanAmountPlaceholder?: string
  emptyLoanAmountHelperText?: string
}

export type PropertyBackedLoanHeroContent = {
  breadcrumbLabel: string
  titleFirstLine: string
  titleSecondLine?: string
  titleHighlight: string
  description: ReactNode
  benefits: [string, string, string] | [string, string, string, string]
}

const benefitIconColor = 'var(--colors-cyan-background-pressed)'
const homeRenovationBenefitIconColor = 'var(--colors-blue-icon-default)'

const getDefaultBenefitIcons = (variant: PropertyBackedLoanHeroVariant): [ReactNode, ReactNode, ReactNode] => {
  if (variant === 'home-renovation')
    return [
      <MoneyCircleIcon key="money" color={homeRenovationBenefitIconColor} />,
      <CalendarIcon key="calendar" color={homeRenovationBenefitIconColor} />,
      <SmartphoneIcon key="smartphone" color={homeRenovationBenefitIconColor} />
    ]

  if (variant === 'land-loan')
    return [
      <CalendarIcon key="calendar" color={benefitIconColor} />,
      <UsersGroupIcon key="users" color={benefitIconColor} />,
      <CalculatorIcon key="calculator" color={benefitIconColor} />
    ]

  return [
    <CalculatorIcon key="calculator" color={benefitIconColor} />,
    <MoneyCircleDashIcon key="money" color={benefitIconColor} />,
    <ChatIcon key="chat" color={benefitIconColor} />
  ]
}

const constructionLoanBenefitIcons = [GroundIcon, ComercialBuildingIcon, PersonIcon, UsersGroupIcon]

const minimumValueRules: Record<string, { minimum: number; message: string }> = {
  loanAmount: { minimum: 50000, message: 'O valor mínimo de solicitação de crédito é de R$ 50 mil.' },
  propertyValue: { minimum: 250000, message: 'O valor mínimo de imóvel em garantia é de R$ 250 mil.' }
}

const getMinimumValueError = (field: string, value: FormValues[keyof FormValues]): ValidationError | undefined => {
  const rule = minimumValueRules[field]

  if (!rule || !value || +value >= rule.minimum) return undefined

  return { invalid: rule.message }
}

const defaultHeroContent: PropertyBackedLoanHeroContent = {
  breadcrumbLabel: 'Empréstimo com Garantia de Imóvel',
  titleFirstLine: 'Empréstimo com',
  titleHighlight: 'Garantia de Imóvel',
  description:
    'Veja como fazer Empréstimo com Garantia de Imóvel de forma rápida e descomplicada. Consiga até R$ 25 milhões em crédito livre!',
  benefits: [
    'Taxas pré e pós-fixadas.',
    'Composição de renda e garantia flexíveis.',
    'Análise personalizada do começo ao fim.'
  ]
}

const PropertyBackedLoanHeroComponent = ({
  dataLayerPageName = 'Empréstimo com Garantia de Imóvel',
  content = defaultHeroContent,
  variant = 'default',
  benefitIcons,
  formConfig
}: Props) => {
  const router = useRouter()
  const isHomeRenovation = variant === 'home-renovation'
  const resolvedBenefitIcons = benefitIcons ?? getDefaultBenefitIcons(variant)
  const loanAmountPlaceholder = formConfig?.loanAmountPlaceholder ?? (isHomeRenovation ? '50.000' : '100.000')
  const emptyLoanAmountHelperText =
    formConfig?.emptyLoanAmountHelperText ?? (isHomeRenovation ? 'cinquenta mil reais' : 'cem mil reais')
  const textColor = isHomeRenovation ? 'var(--colors-neutral-text-default)' : 'var(--colors-primary-text-strong)'

  const [formValues, setFormValues] = useState<FormValues>({
    propertyValue: '',
    loanAmount: '',
    state: '',
    personType: ProspectPersonType.NATURAL_PERSON
  })
  const [formErrors, setFormErrors] = useState<FormErrors>({ propertyValue: {}, loanAmount: {}, state: {} })
  const [changedField, setChangedField] = useState<string>('')

  const validateField = useCallback(
    (field: string) => {
      let error: ValidationError = { valid: '' }
      switch (field) {
        case 'loanAmount':
          if (!formValues.loanAmount) error = { invalid: 'Preencha esse campo para continuar.' }
          else if (+formValues.loanAmount >= 1000000000)
            error = {
              invalid: 'O valor do empréstimo deve ser menor que R$ 1.000.000.000'
            }
          break
        case 'propertyValue':
          if (!formValues.propertyValue) error = { invalid: 'Preencha esse campo para continuar.' }
          else if (+formValues.propertyValue > 2000000000)
            error = { invalid: 'O valor do imóvel pode ser no máximo R$ 2.000.000.000' }
          break
        case 'state':
        default:
          if (!formValues.state) error = { invalid: 'Preencha esse campo para continuar.' }
          break
      }

      setChangedField('')
      if (['loanAmount', 'propertyValue', 'state'].includes(field))
        setFormErrors(errors => ({ ...errors, [field]: error }))
    },
    [formValues]
  )

  const validateFieldOnBlur = useCallback(
    (field: string) => () => {
      const error = getMinimumValueError(field, formValues[field])

      if (error) setFormErrors(errors => ({ ...errors, [field]: error }))
    },
    [formValues]
  )

  const validateFields = useCallback(() => {
    const newErrors: Partial<FormErrors> = {
      propertyValue: { valid: '' },
      loanAmount: { valid: '' },
      state: { valid: '' }
    }

    if (!formValues.loanAmount) newErrors['loanAmount'] = { invalid: 'Preencha esse campo para continuar.' }
    else if (+formValues.loanAmount >= 1000000000)
      newErrors['loanAmount'] = {
        invalid: 'O valor do empréstimo deve ser menor que R$ 1.000.000.000'
      }

    if (!formValues.propertyValue) newErrors['propertyValue'] = { invalid: 'Preencha esse campo para continuar.' }
    else if (+formValues.propertyValue > 2000000000)
      newErrors['propertyValue'] = { invalid: 'O valor do imóvel pode ser no máximo R$ 2.000.000.000' }

    if (!formValues.state) newErrors['state'] = { invalid: 'Preencha esse campo para continuar.' }

    const loanAmountMinimumError = getMinimumValueError('loanAmount', formValues.loanAmount)
    const propertyValueMinimumError = getMinimumValueError('propertyValue', formValues.propertyValue)

    if (loanAmountMinimumError) newErrors['loanAmount'] = loanAmountMinimumError
    if (propertyValueMinimumError) newErrors['propertyValue'] = propertyValueMinimumError

    setFormErrors(errors => ({
      ...errors,
      ...newErrors
    }))
  }, [formValues])

  const handleChange = useCallback(
    (field: string) => (e: ChangeEvent<HTMLInputElement>, textfieldValue?: string) => {
      const sanitizedValue = textfieldValue?.replaceAll(/\D/g, '') ?? ''

      if (+sanitizedValue < 10000000000)
        setFormValues({ ...formValues, [field]: sanitizedValue ? +sanitizedValue : '' })
      setChangedField(field)
    },
    [formValues]
  )

  const handleStateChange = useCallback(
    value => {
      setFormValues({ ...formValues, state: value })
      setChangedField('state')
    },
    [formValues]
  )

  const getSelectStatus = useCallback(
    () => (formErrors.state?.valid === '' ? 'success' : formErrors.state?.invalid ? 'error' : 'default'),
    [formErrors]
  )

  const isFormValid = useCallback(
    () =>
      formErrors.loanAmount?.valid === '' && formErrors.propertyValue?.valid === '' && formErrors.state?.valid === '',
    [formErrors.loanAmount?.valid, formErrors.propertyValue?.valid, formErrors.state?.valid]
  )

  const handleSubmit = useCallback(() => {
    validateFields()

    const hasMinimumValueError =
      getMinimumValueError('loanAmount', formValues.loanAmount) ||
      getMinimumValueError('propertyValue', formValues.propertyValue)

    if (!isFormValid() || hasMinimumValueError) return

    setSessionValue('propertyValue', `${formValues.propertyValue}`)
    setSessionValue('loanAmount', `${formValues.loanAmount}`)
    setSessionValue('personType', `${formValues.personType}`)
    setSessionValue('state', `${formValues.state}`)

    pushDataLayer({
      category: 'Clique',
      action: 'Botões',
      label: `Banner - ${dataLayerPageName} - Simular agora`
    })

    location.href = buildSimulatorUrl(router.pathname, { medium: 'Banner-Simular agora' }, true)
  }, [dataLayerPageName, formValues, isFormValid, validateFields, router.pathname])

  const BreadcrumbLinks = [{ label: content.breadcrumbLabel }]

  const getHelperText = useCallback(
    (field: string) => {
      if (formValues[field]) return extenso(formValues[field], { mode: 'currency' })
      if (formValues[field] === '' && field === 'propertyValue') return 'duzentos e cinquenta mil reais'
      if (formValues[field] === '' && field === 'loanAmount') return emptyLoanAmountHelperText

      return 'zero reais'
    },
    [emptyLoanAmountHelperText, formValues]
  )

  useEffect(() => {
    if (changedField) validateField(changedField)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues, changedField])

  return (
    <S.Hero id="hero-section" $variant={variant}>
      <Grid col={{ phone: 1, tablet: 2 }}>
        <Col col={{ desktop: 5 }} start={{ desktop: 2 }}>
          <S.Infos $variant={variant}>
            <Breadcrumb
              items={BreadcrumbLinks}
              linkColor={isHomeRenovation ? 'primary-text-default' : 'secondary-text-contrast'}
              textColor={isHomeRenovation ? 'neutral-text-default' : 'primary-text-strong'}
            />
            <Text as="h1" size="heading2" weight="medium" color={textColor}>
              {content.titleFirstLine} <br />
              {content.titleSecondLine ? `${content.titleSecondLine} ` : ''}
              <S.TitleHighlight $variant={variant}>{content.titleHighlight}</S.TitleHighlight>
            </Text>
            <Text color={textColor}>{content.description}</Text>
            <S.LoanInfos>
              {variant === 'construction-loan' ? (
                content.benefits.map((benefit, index) => {
                  const BenefitIcon = constructionLoanBenefitIcons[index]

                  return (
                    <S.LoanInfosItem key={benefit}>
                      <BenefitIcon color="var(--colors-cyan-background-pressed)" />
                      <Text color="var(--colors-primary-text-strong)">{benefit}</Text>
                    </S.LoanInfosItem>
                  )
                })
              ) : (
                content.benefits.map((benefit, index) => (
                  <S.LoanInfosItem key={benefit}>
                    {resolvedBenefitIcons[index]}
                    <Text color={textColor}>{benefit}</Text>
                  </S.LoanInfosItem>
                ))
              )}
            </S.LoanInfos>
          </S.Infos>
        </Col>
        <Col col={{ desktop: 5 }} start={{ desktop: 8 }}>
          {!isHomeRenovation && (
            <S.PolygonWrapper>
              <S.Polygon type="TriangleRound" draw="fill" color="#fa0890" />
            </S.PolygonWrapper>
          )}
          <S.RightWrapper>
            <S.FormWrapper $variant={variant}>
              <S.FormHeading size="heading4" weight="medium" color="var(--colors-neutral-text-default)">
                <span>1</span>Sobre o crédito
              </S.FormHeading>
              <S.FieldWrapper>
                <Text weight="medium" color="var(--colors-neutral-text-default)">
                  De quanto você precisa?
                </Text>
                <TextField
                  id="loanAmount"
                  name="loanAmount"
                  mask="number"
                  prefix="R$"
                  placeholder={loanAmountPlaceholder}
                  validation={formErrors.loanAmount}
                  value={formValues.loanAmount ? formValues.loanAmount : undefined}
                  onChange={handleChange('loanAmount')}
                  onBlur={validateFieldOnBlur('loanAmount')}
                  helperText={
                    <>
                      O valor desejado é de <b>{getHelperText('loanAmount')}</b>.
                    </>
                  }
                  ghostMode
                  inputMode="numeric"
                  pattern="[0-9]*"
                  required
                />
              </S.FieldWrapper>
              <S.RadioWrapper>
                <Text weight="medium" color="var(--colors-neutral-text-default)">
                  A contratação de crédito será feita por:
                </Text>
                <RadioButton
                  checked={formValues.personType === ProspectPersonType.NATURAL_PERSON}
                  name="personType"
                  value={ProspectPersonType.NATURAL_PERSON}
                  onChange={() => setFormValues({ ...formValues, personType: ProspectPersonType.NATURAL_PERSON })}
                >
                  Você
                </RadioButton>
                <RadioButton
                  checked={formValues.personType === ProspectPersonType.LEGAL_PERSON}
                  name="personType"
                  value={ProspectPersonType.LEGAL_PERSON}
                  onChange={() => setFormValues({ ...formValues, personType: ProspectPersonType.LEGAL_PERSON })}
                >
                  Sua empresa
                </RadioButton>
              </S.RadioWrapper>
              <S.FormHeading size="heading4" weight="medium" color="var(--colors-neutral-text-default)">
                <span>2</span>Sobre o imóvel
              </S.FormHeading>
              <S.FieldWrapper>
                <Text weight="medium" color="var(--colors-neutral-text-default)">
                  Qual é o valor aproximado do seu imóvel?
                </Text>
                <TextField
                  id="propertyValue"
                  name="propertyValue"
                  mask="number"
                  prefix="R$"
                  placeholder="250.000"
                  validation={formErrors.propertyValue}
                  value={formValues.propertyValue ? formValues.propertyValue : undefined}
                  onChange={handleChange('propertyValue')}
                  onBlur={validateFieldOnBlur('propertyValue')}
                  helperText={
                    <>
                      Seu imóvel vale <b>{getHelperText('propertyValue')}</b>.
                    </>
                  }
                  ghostMode
                  inputMode="numeric"
                  pattern="[0-9]*"
                  required
                />
              </S.FieldWrapper>
              <S.FieldWrapper>
                <Text weight="medium" color="var(--colors-neutral-text-default)">
                  Onde está localizado o imóvel de garantia?
                </Text>
                <SelectDropdown
                  id="state"
                  name="state"
                  label=""
                  placeholder="Selecione o estado"
                  value={formValues.state}
                  helperText={formErrors.state?.invalid ?? ''}
                  options={stateOptions}
                  status={getSelectStatus()}
                  type="default"
                  onChange={handleStateChange}
                />
              </S.FieldWrapper>
              <Button size="lg" type="button" onClick={handleSubmit} variants="text-only">
                Simular agora
              </Button>
            </S.FormWrapper>
          </S.RightWrapper>
        </Col>
      </Grid>
    </S.Hero>
  )
}

export const PropertyBackedLoanHero = memo(PropertyBackedLoanHeroComponent)

PropertyBackedLoanHero.displayName = 'PropertyBackedLoanHero'
